import produce from 'immer';
import {
  COLLABORATION_CLEAR_STORE,
  COLLABORATION_UPDATE_USERS,
  COLLABORATION_INITIAL_LOAD,
  COLLABORATION_UPDATE_CHANNEL,
  COLLABORATION_SWITCH_CHANNEL,
  COLLABORATION_SET_MENTION_ID,
  COLLABORATION_SET_ACTIVE_TAG,
  COLLABORATION_HAS_NEW_MESSAGES,
  COLLABORATION_SCROLL_TO_BOTTOM,
  COLLABORATION_SET_GLOBAL_LOADER,
  COLLABORATION_CLEAR_NEW_MESSAGES,
  COLLABORATION_SET_PREPARED_CANVAS,
  COLLABORATION_SET_REPLY_MESSAGE_ID,
  COLLABORATION_SET_HAS_MORE_MESSAGES,
  COLLABORATION_SET_MESSAGES_IN_STORE,
  COLLABORATION_SET_MESSAGE_DECORATOR,
  COLLABORATION_SET_HIGHLIGHT_MESSAGE,
  COLLABORATION_UPDATE_DIRECT_CHANNELS,
} from '../../actionTypes/collaboration';
import { PayloadFromCreator } from '../../utils/types';
import {
  initialLoadAction,
  scrollToBottomAction,
  setActiveTagAction,
  setGlobalLoaderAction,
  setHasMoreMessagesAction,
  setHighlightMessageAction,
  setMentionIdAction,
  setMessageDecoratorAction,
  setMessagesInStoreAction,
  setPreparedCanvasAction,
  setReplyMessageIdAction,
  switchChannelAction,
  updateChannelAction,
  updateDirectChannelsAction,
  updateUsersAction,
} from '../../actions/collaboration';

const defaultState = {
  messages: [],
  activeTag: '',
  isLoading: true,
  loadedChannelId: '',
  mainChannelId: '',
  currentChannelId: '',
  currentChannelName: '',
  currentChannelKind: '',
  lastViewedAt: '',
  hasNewMessages: false,
  hasMoreMessages: false,
  decorator: { name: '', id: '' },
  highlightMessage: '',
  mentionId: '',
  replyMessageId: '',
  userList: [],
  workspaceMeta: {},
};

export default (state: any = defaultState, action: any) =>
  produce(state, (draft: any) => {
    switch (action.type) {
      case COLLABORATION_INITIAL_LOAD: {
        const { userList, workspace, channel } =
          action.payload as PayloadFromCreator<typeof initialLoadAction>;
        if (!channel) {
          /* We populate the collaboration from workspace (Initial page load) */
          const main = workspace.main;
          draft.currentChannelId = main.id;
          draft.currentChannelName = 'MAIN';
          draft.currentChannelKind = 'MAIN';
          draft.mainChannelId = main.id;
          draft.lastViewedAt = main.lastViewedAt;
          return;
        }
        draft.userList = userList;
        draft.loadedChannelId = channel.id;
        draft.currentChannelId = channel.id;
        draft.currentChannelName = channel.name;
        draft.currentChannelKind = channel.kind;
        draft.lastViewedAt = channel.lastViewedAt;
        draft.messages = channel.messages;
        draft.workspaceMeta = workspace;
        return;
      }
      case COLLABORATION_SET_MESSAGES_IN_STORE: {
        const messages = action.payload as PayloadFromCreator<
          typeof setMessagesInStoreAction
        >;
        draft.messages = messages;
        return;
      }
      case COLLABORATION_SET_ACTIVE_TAG: {
        const newActiveTag = action.payload as PayloadFromCreator<
          typeof setActiveTagAction
        >;
        draft.activeTag = newActiveTag;
        return;
      }
      case COLLABORATION_UPDATE_CHANNEL: {
        const { channel, channelView } = action.payload as PayloadFromCreator<
          typeof updateChannelAction
        >;
        const { id, kind } = channel;
        const { entity } = channelView;
        const lastViewedAt = entity?.viewedAt;
        const lastViewedAtWithDelay = lastViewedAt - 3;
        if (kind === 'MAIN' && draft?.workspaceMeta?.main) {
          draft.workspaceMeta.main.lastViewedAt = lastViewedAtWithDelay;
        } else {
          const directChannel = draft?.workspaceMeta?.directs?.find(
            (currentDirectChannel: any) => currentDirectChannel.id === id,
          );
          if (directChannel) {
            directChannel.lastViewedAt = lastViewedAtWithDelay;
          }
        }
        draft.lastViewedAt = lastViewedAtWithDelay;
        return;
      }
      case COLLABORATION_HAS_NEW_MESSAGES: {
        draft.hasNewMessages = true;
        return;
      }
      case COLLABORATION_CLEAR_NEW_MESSAGES: {
        draft.hasNewMessages = false;
        return;
      }
      case COLLABORATION_SET_HAS_MORE_MESSAGES: {
        const hasMoreMessages = action.payload as PayloadFromCreator<
          typeof setHasMoreMessagesAction
        >;
        draft.hasMoreMessages = hasMoreMessages;
        return;
      }
      case COLLABORATION_SWITCH_CHANNEL: {
        const channelId = action.payload as PayloadFromCreator<
          typeof switchChannelAction
        >;
        let channelLastViewedAt = -1;
        if (channelId === state.mainChannelId) {
          channelLastViewedAt = state.workspaceMeta.main.lastViewedAt;
        } else {
          const directChannel = state.workspaceMeta.directs.find(
            (direct: any) => direct.id === channelId,
          );
          if (directChannel) {
            channelLastViewedAt = directChannel.lastViewedAt;
          }
        }
        draft.currentChannelId = channelId;
        draft.activeTag = '';
        draft.messages = [];
        draft.hasMoreMessages = false;
        draft.hasNewMessages = false;
        draft.decorator = { name: '', id: '' };
        draft.currentLoadedChannelData = {};
        draft.lastViewedAt = channelLastViewedAt;
        draft.isLoading = true;
        return;
      }
      case COLLABORATION_SET_MESSAGE_DECORATOR: {
        const newDecorator = action.payload as PayloadFromCreator<
          typeof setMessageDecoratorAction
        >;
        draft.decorator = newDecorator;
        return;
      }
      case COLLABORATION_UPDATE_USERS: {
        const userList = action.payload as PayloadFromCreator<
          typeof updateUsersAction
        >;
        draft.userList = userList;
        return;
      }
      case COLLABORATION_UPDATE_DIRECT_CHANNELS: {
        const directChannels = action.payload as PayloadFromCreator<
          typeof updateDirectChannelsAction
        >;
        draft.workspaceMeta.directs = directChannels;
        return;
      }
      case COLLABORATION_CLEAR_STORE: {
        return defaultState;
      }
      case COLLABORATION_SCROLL_TO_BOTTOM: {
        const newState = action.payload as PayloadFromCreator<
          typeof scrollToBottomAction
        >;
        draft.scrollToBottom = newState;
        return;
      }
      case COLLABORATION_SET_HIGHLIGHT_MESSAGE: {
        const messageId = action.payload as PayloadFromCreator<
          typeof setHighlightMessageAction
        >;
        draft.highlightMessage = messageId;
        return;
      }
      case COLLABORATION_SET_GLOBAL_LOADER: {
        const loadingState = action.payload as PayloadFromCreator<
          typeof setGlobalLoaderAction
        >;
        draft.isLoading = loadingState;
        return;
      }
      case COLLABORATION_SET_MENTION_ID: {
        const mentionId = action.payload as PayloadFromCreator<
          typeof setMentionIdAction
        >;
        draft.mentionId = mentionId;
        return;
      }
      case COLLABORATION_SET_REPLY_MESSAGE_ID: {
        const replyMessageId = action.payload as PayloadFromCreator<
          typeof setReplyMessageIdAction
        >;
        draft.replyMessageId = replyMessageId;
        return;
      }
      case COLLABORATION_SET_PREPARED_CANVAS: {
        const canvas = action.payload as PayloadFromCreator<
          typeof setPreparedCanvasAction
        >;
        draft.preparedCanvas = canvas;
        return;
      }
    }
  });
